import { useMemo } from 'react'
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { ATTRIBUTION_GROUP_BYS, type AttributionGroupBy, type AttributionQuery, type AttributionReport } from '@/api/attribution'
import type { ApiClient, ApiClientKind } from '@/api/client'
import type { FirstPartyTrafficQuery, FirstPartyTrafficReport } from '@/api/firstPartyTraffic'
import { ATTRIBUTION_MODELS, type AttributionModel } from '@/api/types'
import { resolvePeriod, type GlobalFilters } from '@/lib/filters'
import { useUrlChoice } from '@/lib/useUrlChoice'

/** Blueprint 05 §5.8's default, and the API's. */
export const DEFAULT_MODEL: AttributionModel = 'last_non_direct'
export const DEFAULT_GROUP_BY: AttributionGroupBy = 'campaign'

export const ATTRIBUTION_VIEWS = ['table', 'compare'] as const
export type AttributionView = (typeof ATTRIBUTION_VIEWS)[number]

export const ATTRIBUTION_VIEW_LABELS: Record<AttributionView, string> = {
  table: 'One model',
  compare: 'Compare models',
}

/** Channels are a short, fixed list (`channels.CHANNEL_LABELS`): one page holds them all. */
const CHANNEL_LIMIT = 100

/**
 * Every React Query key this page reads. One per query, so the comparison view's
 * four models share the table's cache: the selected model is never fetched twice.
 */
export const attributionKeys = {
  all: ['attribution'] as const,
  report: (kind: ApiClientKind, query: AttributionQuery) => [...attributionKeys.all, 'conversions', kind, query] as const,
  channels: (kind: ApiClientKind, query: FirstPartyTrafficQuery) => [...attributionKeys.all, 'channels', kind, query] as const,
}

export function useModel(): [AttributionModel, (next: AttributionModel) => void] {
  return useUrlChoice('attr_model', ATTRIBUTION_MODELS, DEFAULT_MODEL)
}

export function useGroupBy(): [AttributionGroupBy, (next: AttributionGroupBy) => void] {
  return useUrlChoice('attr_group', ATTRIBUTION_GROUP_BYS, DEFAULT_GROUP_BY)
}

export function useView(): [AttributionView, (next: AttributionView) => void] {
  return useUrlChoice('attr_view', ATTRIBUTION_VIEWS, 'table')
}

/** The report query for a filter state: the filter bar's range and previous-period comparison, this page's model and grouping. */
export function attributionQuery(
  filters: GlobalFilters,
  model: AttributionModel,
  groupBy: AttributionGroupBy,
  now = new Date(),
): AttributionQuery {
  const { current } = resolvePeriod(filters, now)
  return { from: current.from, to: current.to, model, groupBy, compare: filters.compare === 'previous_period' }
}

export function useAttributionQuery(filters: GlobalFilters, model: AttributionModel, groupBy: AttributionGroupBy): AttributionQuery {
  return useMemo(() => attributionQuery(filters, model, groupBy), [filters, model, groupBy])
}

/**
 * The one way this page reads a report: its key from `attributionKeys`, its fetch from
 * the client — so the table, the comparison's four models and the channel table cannot
 * disagree about the cache.
 */
function conversionsOptions(client: ApiClient, query: AttributionQuery, enabled = true) {
  return {
    queryKey: attributionKeys.report(client.kind, query),
    queryFn: () => client.attributionConversions(query),
    enabled,
  }
}

export function useAttributionReport(query: AttributionQuery): UseQueryResult<AttributionReport> {
  return useQuery(conversionsOptions(useApiClient(), query))
}

export interface ModelComparisonReads {
  /** Every model's report, in `ATTRIBUTION_MODELS` order; undefined until all four have loaded. */
  reports: Array<{ model: AttributionModel; report: AttributionReport }> | undefined
  isPending: boolean
  /** The first failure among the three, if any. */
  error: unknown
}

/** The same range and grouping under each of the four models, fetched in parallel once `enabled`. */
export function useModelComparison(base: AttributionQuery, enabled = true): ModelComparisonReads {
  const client = useApiClient()
  const results = useQueries({
    queries: ATTRIBUTION_MODELS.map((model) => conversionsOptions(client, { ...base, model }, enabled)),
  })
  const failed = results.find((result) => result.isError)
  const loaded = results.every((result) => result.data !== undefined)
  return {
    reports: loaded
      ? ATTRIBUTION_MODELS.map((model, index) => ({ model, report: results[index]!.data as AttributionReport }))
      : undefined,
    isPending: !failed && !loaded,
    error: failed?.error ?? null,
  }
}

/**
 * The channel table's query: the first-party traffic route at `level=channel`, under
 * this page's model. Production only like the conversions above, whatever the filter
 * bar's environment says, so the two tables on one page never count different data.
 */
export function channelQuery(base: AttributionQuery): FirstPartyTrafficQuery {
  return {
    from: base.from,
    to: base.to,
    compare: base.compare,
    model: base.model,
    level: 'channel',
    path: [],
    environments: [],
    limit: CHANNEL_LIMIT,
  }
}

export function useChannelReport(base: AttributionQuery): UseQueryResult<FirstPartyTrafficReport> {
  const client = useApiClient()
  const query = useMemo(() => channelQuery(base), [base])
  return useQuery({ queryKey: attributionKeys.channels(client.kind, query), queryFn: () => client.firstPartyTraffic(query) })
}
